import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";


const FEATURE_CARDS = [
  {
    Icon: DescriptionIcon,
    title: "Survey Templates",
    route: "/survey-templates",
    accent: "#0e7490",
    accentBg: "rgba(14, 116, 144, 0.08)",
    bullets: [
      "Reusable frameworks for consistent outreach",
      "Customizable surveys tailored to your goals",
      "Standardize BR&E outreach across your region",
      "Advanced sector & industry tagging",
      "Public & internal sharing with access control",
      "Multi-choice and open-ended response support",
    ],
  },
  {
    Icon: ListAltIcon,
    title: "Survey Responses",
    route: "/survey-responses",
    accent: "#2563eb",
    accentBg: "rgba(37, 99, 235, 0.08)",
    bullets: [
      "View all survey submissions in one place",
      "Capture detailed company & contact metadata",
      "Advanced filtering & sorting for fast analysis",
      "Create follow-up tasks directly from responses",
      "Export data & generate print-ready reports",
      "Identify urgent business needs quickly",
    ],
  },
  {
    Icon: BarChartIcon,
    title: "Survey Analytics",
    route: "/survey-stats",
    accent: "#7c3aed",
    accentBg: "rgba(124, 58, 237, 0.07)",
    bullets: [
      "Interactive trend charts & line graph visualizations",
      "Filter by survey template & custom date ranges",
      "Regional & industry-level performance analysis",
      "Identify common challenges & recurring issues",
      "Generate board-ready reports for leadership",
      "Track yearly progress & long-term improvement",
    ],
  },
  {
    Icon: AutoAwesomeIcon,
    title: "AI Insights",
    route: null,
    accent: BRAND_GREEN,
    accentBg: "rgba(100, 131, 21, 0.08)",
    bullets: [
      "AI-generated summaries of key response themes",
      "Instant analysis with no manual processing",
      "Detect emerging trends across your region",
      "Brief executives with clear, actionable insights",
      "Integrated with dashboards & reporting tools",
      "Reduce manual reporting & save staff time",
    ],
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Build Templates",
    desc: "Design reusable survey frameworks tailored to your BR&E strategy and outreach goals.",
    Icon: DescriptionIcon,
  },
  {
    step: "02",
    title: "Collect Responses",
    desc: "Gather detailed feedback from businesses across your region in one centralized place.",
    Icon: ListAltIcon,
  },
  {
    step: "03",
    title: "Analyze & Act",
    desc: "Surface trends with analytics and AI insights, then create follow-up tasks instantly.",
    Icon: AutoAwesomeIcon,
  },
];

export default function BRESurveys() {
  const navigate = useNavigate();

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: "#f6f9ff", pb: 6 }}>

      {/* ── Sticky Header ── */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            position: "relative",
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
              }}
            >
              <AssignmentIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: 21,
                fontWeight: 800,
                color: BRAND_BLUE,
                lineHeight: 1.05,
                whiteSpace: "nowrap",
              }}
            >
              Surveys
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              position: { xs: "static", md: "absolute" },
              right: { md: 24 },
              top: { md: "50%" },
              transform: { md: "translateY(-50%)" },
            }}
          >
            {[
              { label: "Templates", Icon: DescriptionIcon, route: "/survey-templates" },
              { label: "Responses", Icon: ListAltIcon, route: "/survey-responses" },
              { label: "Analytics", Icon: BarChartIcon, route: "/survey-stats" },
            ].map(({ label, Icon, route }) => (
              <Button
                key={label}
                startIcon={<Icon sx={{ fontSize: "15px !important" }} />}
                onClick={() => navigate(route)}
                sx={{
                  minHeight: 34,
                  px: 1.5,
                  borderRadius: 2,
                  border: "1px solid #dce4ef",
                  color: "#173153",
                  backgroundColor: "#ffffff",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 13,
                  "&:hover": { backgroundColor: "#f7f9fc", borderColor: BRAND_BLUE },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

        </Box>
      </Box>

      {/* ── Hero Banner ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1d4f91 60%, #0e3a6e 100%)`,
          px: { xs: 3, md: 6 },
          py: { xs: 5, md: 7 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 20% 50%, rgba(100,131,21,0.15) 0%, transparent 55%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 45%)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 720, mx: "auto" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(100, 131, 21, 0.2)",
              border: "1px solid rgba(100, 131, 21, 0.4)",
              borderRadius: "20px",
              px: 1.5,
              py: 0.5,
              mb: 2.5,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 14, color: "#a8d060" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#a8d060", letterSpacing: 0.5 }}>
              BR&E SURVEY PLATFORM
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "1.6rem", md: "2.1rem" },
              fontWeight: 800,
              color: "#ffffff",
              lineHeight: 1.2,
              mb: 1.5,
            }}
          >
            Turn Conversations Into{" "}
            <Box component="span" sx={{ color: "#a8d060" }}>
              Actionable Insights
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "0.9rem", md: "1rem" },
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.65,
              maxWidth: 580,
              mx: "auto",
            }}
          >
            Collect detailed feedback across your region, identify business needs,
            and leverage AI-driven analysis to support your BR&E strategy.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 3.5 }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/survey-templates")}
              sx={{
                backgroundColor: BRAND_GREEN,
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 2.5,
                px: 3,
                py: 1.1,
                boxShadow: "0 6px 18px rgba(100,131,21,0.35)",
                "&:hover": { backgroundColor: "#507011" },
              }}
            >
              Browse Templates
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/survey-responses")}
              sx={{
                color: "#ffffff",
                borderColor: "rgba(255,255,255,0.35)",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 2.5,
                px: 3,
                py: 1.1,
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.7)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              View Responses
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── How It Works ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 5 }}>
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            color: BRAND_GREEN,
            textTransform: "uppercase",
            mb: 2.5,
          }}
        >
          How It Works
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: { xs: 2.5, md: 3 },
            width: "100%",
          }}
        >
          {HOW_IT_WORKS.map(({ step, title, desc, Icon }) => (
            <Box
              key={step}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,51,108,0.11)",
                boxShadow: "0 12px 32px rgba(0,51,108,0.08)",
                background:
                  "linear-gradient(180deg, rgba(0,51,108,0.06) 0%, #ffffff 42%)",
                px: { xs: 3, md: 4.5 },
                py: { xs: 3.25, md: 4.25 },
                minHeight: { md: 250 },
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  backgroundColor: BRAND_GREEN,
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: BRAND_GREEN,
                  boxShadow: "0 18px 40px rgba(0,51,108,0.14)",
                },
              }}
            >
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: "50%",
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(0,51,108,0.16)",
                  boxShadow: "0 10px 24px rgba(0,51,108,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.25,
                  color: BRAND_BLUE,
                }}
              >
                <Icon sx={{ fontSize: 33 }} />
              </Box>
              <Typography
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 25,
                  borderRadius: 999,
                  backgroundColor: "rgba(100, 131, 21, 0.1)",
                  border: "1px solid rgba(100, 131, 21, 0.18)",
                  px: 1.2,
                  fontSize: "0.64rem",
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  color: BRAND_GREEN,
                  textTransform: "uppercase",
                  mb: 1.15,
                }}
              >
                Step {step}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: BRAND_BLUE, fontSize: "1.24rem", mb: 1.1 }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", color: "#244466", lineHeight: 1.65 }}>
                {desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Feature Cards ── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 4 }}>
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.2,
            color: BRAND_GREEN,
            textTransform: "uppercase",
            mb: 2.5,
          }}
        >
          Platform Features
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {FEATURE_CARDS.map(({ Icon, title, route, accent, accentBg, bullets }) => (
            <Card
              key={title}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,51,108,0.11)",
                boxShadow: "0 12px 32px rgba(0,51,108,0.08)",
                background: `linear-gradient(180deg, ${accentBg} 0%, #ffffff 34%)`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  backgroundColor: accent,
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: accent,
                  boxShadow: "0 18px 40px rgba(0,51,108,0.14)",
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3.25 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                {/* Card header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    pb: 2.25,
                    mb: 2.25,
                    borderBottom: "1px solid rgba(0,51,108,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2.5,
                      backgroundColor: "#ffffff",
                      border: `1px solid ${accent}`,
                      boxShadow: `0 8px 18px ${accentBg}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: accent,
                    }}
                  >
                    <Icon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        letterSpacing: 1,
                        color: accent,
                        textTransform: "uppercase",
                        mb: 0.4,
                      }}
                    >
                      Surveys
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.28rem",
                        fontWeight: 800,
                        color: BRAND_BLUE,
                        lineHeight: 1.15,
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Stack>

                {/* Bullet list */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 1,
                    mb: 2.75,
                  }}
                >
                  {bullets.map((text) => (
                    <Stack
                      key={text}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        minHeight: 48,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.78)",
                        border: "1px solid rgba(0,51,108,0.07)",
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: accentBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: "2px",
                          color: accent,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 12 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.92rem",
                          lineHeight: 1.45,
                          color: BRAND_BLUE,
                        }}
                      >
                        {text}
                      </Typography>
                    </Stack>
                  ))}
                </Box>

                {/* CTA button */}
                {route && (
                  <Box sx={{ mt: "auto" }}>
                    <Button
                      endIcon={<ArrowForwardIcon sx={{ fontSize: "15px !important" }} />}
                      onClick={() => navigate(route)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.86rem",
                        color: "#ffffff",
                        backgroundColor: accent,
                        borderRadius: 2,
                        px: 1.75,
                        py: 0.85,
                        "&:hover": { backgroundColor: accent, opacity: 0.9 },
                      }}
                    >
                      Open {title}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
